import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";

// =============================================================================
// Agent IA « Noa » : conversations, streaming SSE, actions en attente.
// =============================================================================
// Le chat passe par l'edge function `assistant-chat` en fetch direct (streaming
// SSE, non supporté par supabase.functions.invoke). Les tables assistant_* ne
// sont pas dans database.types.ts : pattern db() cast.
// =============================================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = () => supabase as any;

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------
export interface AssistantConversation {
  id: string;
  title: string | null;
  page_context: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AssistantMessage {
  id: string;
  conversation_id: string;
  role: "user" | "assistant" | "tool";
  content: string | null;
  tool_calls: { name: string; status: string; error?: string }[] | null;
  pending_action_id: string | null;
  created_at: string;
}

export interface AssistantPendingAction {
  id: string;
  conversation_id: string;
  tool_name: string;
  args: Record<string, unknown>;
  summary: string | null;
  status: "pending" | "confirmed" | "rejected" | "executed" | "failed" | "expired";
  result: Record<string, unknown> | null;
  expires_at: string;
  created_at: string;
}

export interface AssistantPageContext {
  route: string;
  space?: string;
  dossierId?: string;
  salarieId?: string;
}

export type AssistantStreamEvent =
  | { type: "conversation"; conversationId: string }
  | { type: "delta"; text: string }
  | { type: "final"; text: string }
  | { type: "tool"; name: string; status: "running" | "done" | "error"; error?: string }
  | { type: "pending_action"; action: AssistantPendingAction }
  | { type: "done"; conversationId: string; messageId: string | null }
  | { type: "error"; error: string };

// -----------------------------------------------------------------------------
// Streaming (fetch SSE direct sur l'edge function)
// -----------------------------------------------------------------------------
function functionsUrl(): string {
  const base = import.meta.env.VITE_SUPABASE_URL as string;
  return `${base}/functions/v1/assistant-chat`;
}

async function authToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Session expirée : reconnectez-vous.");
  return token;
}

/**
 * Envoie un message et stream la réponse. `onEvent` reçoit chaque événement SSE.
 * Retourne quand le stream est terminé. `signal` permet d'annuler.
 */
export async function streamAssistantChat(args: {
  conversationId?: string | null;
  message: string;
  pageContext?: AssistantPageContext;
  onEvent: (event: AssistantStreamEvent) => void;
  signal?: AbortSignal;
}): Promise<void> {
  const token = await authToken();
  const res = await fetch(functionsUrl(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      action: "chat",
      conversationId: args.conversationId ?? undefined,
      message: args.message,
      pageContext: args.pageContext,
    }),
    signal: args.signal,
  });

  if (!res.ok || !res.body) {
    const json = await res.json().catch(() => null);
    throw new Error(
      (json as { error?: string } | null)?.error ?? `Assistant indisponible (HTTP ${res.status})`,
    );
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() ?? "";
    for (const part of parts) {
      const dataLine = part
        .split("\n")
        .filter((l) => l.startsWith("data:"))
        .map((l) => l.slice(5).trim())
        .join("");
      if (!dataLine) continue;
      try {
        args.onEvent(JSON.parse(dataLine) as AssistantStreamEvent);
      } catch {
        // Ligne non JSON : ignorée.
      }
    }
  }
}

/** Confirme ou rejette une action d'écriture en attente. */
export async function confirmAssistantAction(args: {
  conversationId: string;
  actionId: string;
  approve: boolean;
}): Promise<{
  status: string;
  error: string | null;
  result: Record<string, unknown> | null;
  followUp: string;
}> {
  const token = await authToken();
  const res = await fetch(functionsUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ action: "confirm", ...args }),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.success) {
    throw new Error((json as { error?: string } | null)?.error ?? `Erreur (HTTP ${res.status})`);
  }
  return json.data;
}

/** Demande la génération du titre de la conversation (modèle léger). */
export async function requestConversationTitle(conversationId: string): Promise<string | null> {
  try {
    const token = await authToken();
    const res = await fetch(functionsUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action: "title", conversationId }),
    });
    const json = await res.json().catch(() => null);
    return json?.data?.title ?? null;
  } catch {
    return null;
  }
}

// -----------------------------------------------------------------------------
// Historique (react-query, RLS owner)
// -----------------------------------------------------------------------------
export function useAssistantConversations() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["assistant", "conversations", user?.id],
    enabled: Boolean(user?.id),
    queryFn: async (): Promise<AssistantConversation[]> => {
      const { data, error } = await db()
        .from("assistant_conversations")
        .select("id, title, page_context, archived_at, created_at, updated_at")
        .is("archived_at", null)
        .order("updated_at", { ascending: false })
        .limit(30);
      if (error) throw error;
      return (data ?? []) as AssistantConversation[];
    },
  });
}

export function useAssistantMessages(conversationId: string | null) {
  return useQuery({
    queryKey: ["assistant", "messages", conversationId],
    enabled: Boolean(conversationId),
    queryFn: async (): Promise<AssistantMessage[]> => {
      const { data, error } = await db()
        .from("assistant_messages")
        .select("id, conversation_id, role, content, tool_calls, pending_action_id, created_at")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as AssistantMessage[];
    },
  });
}

export function usePendingAction(actionId: string | null) {
  return useQuery({
    queryKey: ["assistant", "pending-action", actionId],
    enabled: Boolean(actionId),
    queryFn: async (): Promise<AssistantPendingAction | null> => {
      const { data, error } = await db()
        .from("assistant_pending_actions")
        .select("*")
        .eq("id", actionId)
        .maybeSingle();
      if (error) throw error;
      return (data as AssistantPendingAction) ?? null;
    },
  });
}

export function useArchiveConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      const { error } = await db()
        .from("assistant_conversations")
        .update({ archived_at: new Date().toISOString() })
        .eq("id", conversationId);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["assistant", "conversations"] });
    },
  });
}
